import type { FilterItemProps } from "@jana-technology/platform-ui";
import type { AssetSelectionFilterQuery } from "$/__generated__/graphql";
import type { RiskAssetFiltersState } from "./types";

export const keepStrings = (item: unknown): item is string =>
  typeof item === "string";

export const stripTypenameField = <T extends object>(
  item: T & { __typename?: string },
) => {
  const { __typename, ...rest } = item;

  return rest;
};

export const initSelectedRiskAssetFiltersState = (
  baseFilters: AssetSelectionFilterQuery["assetSelectionFilter"],
): RiskAssetFiltersState => ({
  mostRecentCorrosionLogs:
    baseFilters.mostRecentCorrosionLogs?.filter(keepStrings) ?? [],
  wellAgeRange: baseFilters.wellAgeRange ?? [],
  wellConfigurations: baseFilters.wellConfigurations?.filter(keepStrings) ?? [],
  wellTypes: baseFilters.wellTypes?.filter(keepStrings) ?? [],
  riskQuartiles: baseFilters.riskQuartiles?.map(stripTypenameField) ?? [],
});

export const generateRadioState = (
  radioTreeBreadcrumbs: Array<FilterItemProps>,
): Array<FilterItemProps> => {
  const reduced = radioTreeBreadcrumbs.reduceRight<FilterItemProps | null>(
    (acc, node) => {
      if (acc) {
        return { ...node, children: [acc] };
      }

      return "searchLabel" in node
        ? {
            value: node.value,
            label: node.label,
            searchLabel: node.searchLabel,
          }
        : { value: node.value, label: node.label };
    },
    null,
  );

  return reduced ? [reduced] : [];
};

export const flattenRadioState = (
  radioState: Array<FilterItemProps>,
): Array<FilterItemProps> => {
  const flattened: Array<FilterItemProps> = [];
  const stack: Array<FilterItemProps> = [...radioState].reverse();

  while (stack.length) {
    const node = stack.pop()!;
    flattened.push(node);

    if (node.children?.length) {
      for (let i = node.children.length - 1; i >= 0; i -= 1) {
        stack.push(node.children[i]);
      }
    }
  }

  return flattened;
};

// checks if radio state is the path to a leaf (cycles are ignored since radio state is a tree)
// empty lists are considered paths
export const isRadioStateBreadcrumbsToLeaf = (
  breadcrumbs: Array<FilterItemProps>,
): boolean => {
  if (!breadcrumbs.length) {
    return true;
  } else if (breadcrumbs.length > 1) {
    return false;
  } else {
    return isRadioStateBreadcrumbsToLeaf(breadcrumbs[0].children ?? []);
  }
};
